<template>
  <div class="manage-order-page">
    <template v-if="!isDetail">
      <section class="order-toolbar" :aria-label="$t('manageOrder.filters')">
        <v-menu v-for="filter in dateFilters" :key="filter.key" v-model="dateMenus[filter.key]" :close-on-content-click="false" offset-y min-width="290">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field :value="filters[filter.key]" :placeholder="$t(`manageOrder.${filter.label}`)" :aria-label="$t(`manageOrder.${filter.label}`)" class="order-filter" append-icon="mdi-calendar-clock" readonly clearable solo flat hide-details v-bind="attrs" v-on="on" @click:clear="setDate(filter.key, null)" />
          </template>
          <v-date-picker :value="filters[filter.key]" :locale="$i18n.locale" color="#00378e" @input="setDate(filter.key, $event)" />
        </v-menu>
        <v-select v-model="filters.statusGroup" :items="statusItems" :placeholder="$t('manageOrder.status')" :aria-label="$t('manageOrder.status')" class="order-filter" clearable solo flat hide-details @change="applyFilters" />
        <form class="order-search" @submit.prevent="applyFilters">
          <input v-model.trim="searchInput" type="search" maxlength="100" :placeholder="$t('manageOrder.search')" :aria-label="$t('manageOrder.search')" />
          <button type="submit" :aria-label="$t('manageOrder.search')"><v-icon color="white" size="25">mdi-magnify</v-icon></button>
        </form>
        <v-btn class="order-export" rounded depressed :disabled="loading || exporting || !!listError || totalRecord === 0" @click="exportOrders">{{ $t('manageOrder.export') }}</v-btn>
      </section>
      <v-alert v-if="listError" type="error" text role="alert" class="order-alert">
        {{ listError }} <v-btn small text @click="loadOrders">{{ $t('manageOrder.retry') }}</v-btn>
      </v-alert>
      <section class="order-list-panel" :aria-label="$t('manageOrder.title')">
        <v-data-table :headers="headers" :items="orders" :no-data-text="$t('manageOrder.empty')" :items-per-page="perpage" :server-items-length="totalRecord" item-key="orderID" class="manage-order-table" hide-default-footer disable-sort>
          <template v-slot:item.no="{ index }">{{ (page - 1) * perpage + index + 1 }}</template>
          <template v-slot:item.orderID="{ item }">#{{ item.orderID }}</template>
          <template v-slot:item.locationName="{ item }">{{ locationName(item) }}</template>
          <template v-slot:item.staffName="{ item }">{{ item.staffName || '–' }}</template>
          <template v-slot:item.createTime="{ item }"><span class="order-date">{{ datePart(item.createTime) }}<small>{{ timePart(item.createTime) }}</small></span></template>
          <template v-slot:item.completedTime="{ item }"><span class="order-date">{{ datePart(item.completedTime) }}<small>{{ timePart(item.completedTime) }}</small></span></template>
          <template v-slot:item.status="{ item }">
            <div class="order-status-cell"><span :class="['order-status', statusGroup(item.status)]">{{ statusLabel(item.status) }}</span><small v-if="item.status === 1">{{ $t('manageOrder.preparing') }}</small></div>
          </template>
          <template v-slot:item.action="{ item }"><button class="order-detail-button" type="button" :aria-label="$t('manageOrder.details', { id: item.orderID })" @click="openDetail(item.orderID)"><v-icon color="white" size="24">mdi-arrow-right</v-icon></button></template>
        </v-data-table>
        <footer class="order-pagination">
          <v-pagination v-model="page" :length="pageCount" :total-visible="5" :disabled="loading" color="#00378e" @input="loadOrders" />
          <v-select v-model="perpage" :items="[10, 20, 50, 100]" :aria-label="$t('manageOrder.rowsPerPage')" class="order-page-size" solo flat hide-details :disabled="loading" @change="changePageSize" />
        </footer>
      </section>
    </template>

    <template v-else>
      <div class="order-detail-toolbar">
        <div class="order-detail-heading"><h2>{{ $t('manageOrder.order') }} #{{ $route.params.orderId }}</h2><span v-if="detail" :class="['order-status detail-status', statusGroup(detail.status)]"><span class="status-dot"></span>{{ statusLabel(detail.status) }}</span><small v-if="detail && detail.status === 1">{{ $t('manageOrder.preparing') }}</small></div>
        <div class="order-detail-actions">
          <v-btn v-if="canCancel" class="order-cancel" rounded depressed :disabled="detailLoading || cancelling" @click="openCancel"><v-icon left small>mdi-close-circle-outline</v-icon>{{ $t('manageOrder.cancelOrder') }}</v-btn>
          <v-btn class="order-export" rounded depressed :disabled="!detail || detailLoading || exporting" @click="exportOrders">{{ $t('manageOrder.export') }}</v-btn>
        </div>
      </div>
      <v-alert v-if="detailError" type="error" text role="alert" class="order-alert">{{ detailError }} <v-btn small text @click="loadDetail">{{ $t('manageOrder.retry') }}</v-btn></v-alert>
      <div v-if="detail" class="order-detail-grid">
        <div class="order-information-column">
          <section class="order-information order-card">
            <h3><v-icon color="#0653cc" size="22">mdi-room-service-outline</v-icon>{{ $t('manageOrder.information') }}</h3>
            <div class="order-requester"><span class="order-label">{{ $t('manageOrder.requester') }}</span><div class="requester-content"><img v-if="roomImage && !roomImageFailed" :src="roomImage" :alt="detail.roomName" @error="roomImageFailed = true" /><v-icon v-else class="room-placeholder" color="#00378e">mdi-door-open</v-icon><div><strong>{{ detail.roomName || '–' }}</strong><small>{{ $t('manageOrder.meeting') }} {{ meetingTime }}</small></div></div></div>
            <dl class="order-information-fields">
              <div><dt>{{ $t('manageOrder.zone') }}</dt><dd><img class="order-zone-icon" src="@/assets/icons/container.svg" alt="" aria-hidden="true" />{{ detail.serviceArea || '–' }}</dd></div>
              <div><dt>{{ $t('manageOrder.location') }}</dt><dd>{{ locationName(detail) }}</dd></div>
              <div><dt>{{ $t('manageOrder.orderTime') }}</dt><dd><v-icon small>mdi-calendar-blank-outline</v-icon>{{ detailDate(detail.createTime) }}</dd></div>
              <div><dt>{{ $t('manageOrder.completedTime') }}</dt><dd><v-icon small>mdi-calendar-blank-outline</v-icon>{{ detailDate(detail.completedTime) }}</dd></div>
            </dl>
          </section>
          <section class="order-staff order-card" :aria-label="$t('manageOrder.staff')">
            <div class="staff-profile"><span class="staff-avatar">{{ staffInitials }}</span><div><strong>{{ detail.staffName || $t('manageOrder.unassigned') }}</strong><small>{{ $t('manageOrder.service') }}</small></div></div>
            <div class="staff-service-area"><span>{{ $t('manageOrder.serviceArea') }}</span><strong>{{ detail.serviceArea || '–' }}</strong></div>
          </section>
          <v-alert v-if="detail.cancelReason" type="error" text class="mb-0"><strong>{{ $t('manageOrder.cancellationReason') }}</strong><div class="cancel-reason-text">{{ detail.cancelReason }}</div></v-alert>
        </div>
        <section class="order-items order-card">
          <div class="order-items-heading"><h3><v-icon color="#0653cc" size="22">mdi-coffee-outline</v-icon>{{ $t('manageOrder.items') }}</h3><span>{{ $t('manageOrder.itemSummary', { count: detail.items.length, quantity: totalQuantity }) }}</span></div>
          <div class="order-items-table-header"><span>{{ $t('manageOrder.itemDescription') }}</span><span>{{ $t('manageOrder.quantity') }}</span></div>
          <ul class="order-items-list"><li v-for="item in detail.items" :key="item.itemID"><div><strong>{{ item.beverageName || '–' }}</strong><p>{{ itemSpecifications(item) || '–' }}</p></div><strong class="order-item-quantity">×{{ item.amount }}</strong></li></ul>
        </section>
      </div>
    </template>

    <v-dialog v-model="cancelDialog" max-width="520" :persistent="cancelling">
      <v-card><v-card-title>{{ $t('manageOrder.cancelOrder') }} #{{ detail ? detail.orderID : '' }}</v-card-title><v-card-text><v-textarea v-model="cancelReason" :label="$t('manageOrder.cancelReason')" :hint="$t('manageOrder.cancelHint')" persistent-hint outlined counter="500" maxlength="500" :disabled="cancelling" /><v-alert v-if="cancelError" type="error" text role="alert">{{ cancelError }}</v-alert></v-card-text><v-card-actions class="px-6 pb-5"><v-spacer /><v-btn text :disabled="cancelling" @click="cancelDialog = false">{{ $t('manageOrder.close') }}</v-btn><v-btn color="#c62828" dark :disabled="cancelling || !cancelReason.trim() || cancelReason.length > 500" @click="cancelOrder">{{ $t('manageOrder.confirmCancel') }}</v-btn></v-card-actions></v-card>
    </v-dialog>
    <v-dialog :value="LoadingDialog" persistent width="auto">
      <v-card class="pa-6 d-flex flex-column align-center justify-center" elevation="8" rounded="xl">
        <v-progress-circular indeterminate color="indigo" size="70" width="6" />
        <span class="mt-4 text-subtitle-1 font-weight-medium">{{ $t('infrastructure.loading') }}</span>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="noticeVisible" :timeout="7000" color="error">{{ notice }}<v-btn text @click="noticeVisible = false">{{ $t('manageOrder.close') }}</v-btn></v-snackbar>
  </div>
</template>

<script>
import axios from "axios";
import dayjs from "dayjs";
import enurl from "@/api/environment";

// Preserve the API lifecycle: 0 New, 1 Preparing, 2 Ready, 3 Completed, 4 Cancelled.
const STATUS_GROUPS = ["new", "new", "ready", "completed", "rejected"];
const normalizeFields = (record) => Object.keys(record || {}).reduce((result, key) => {
  result[key.charAt(0).toLowerCase() + key.slice(1)] = record[key];
  return result;
}, {});

export default {
  name: "ManageOrder",
  data() {
    return {
      orders: [], detail: null, page: 1, perpage: 10, totalRecord: 0,
      filters: { orderDate: null, completedDate: null, statusGroup: null, search: "" },
      searchInput: "", dateMenus: { orderDate: false, completedDate: false },
      dateFilters: [{ key: "orderDate", label: "orderTime" }, { key: "completedDate", label: "completedTime" }],
      loading: false, detailLoading: false, exporting: false, cancelling: false,
      listError: "", detailError: "", cancelError: "", cancelDialog: false, cancelReason: "",
      notice: "", noticeVisible: false, roomImageFailed: false,
      listRequest: null, detailRequest: null, exportRequest: null,
    };
  },
  computed: {
    LoadingDialog() { return this.loading || this.detailLoading || this.exporting || this.cancelling; },
    isDetail() { return !!this.$route.params.orderId; },
    pageCount() { return Math.max(1, Math.ceil(this.totalRecord / this.perpage)); },
    statusItems() { return ["new", "ready", "completed", "rejected"].map(value => ({ value, text: this.$t(`manageOrder.${value}`) })); },
    headers() {
      return [
        ["no", "no"], ["orderId", "orderID"], ["requester", "roomName"], ["location", "locationName"],
        ["zone", "serviceArea"], ["orderTime", "createTime"], ["completedTime", "completedTime"],
        ["staff", "staffName"], ["status", "status"], ["action", "action"],
      ].map(([label, value]) => ({ text: this.$t(`manageOrder.${label}`), value, align: ["no", "orderID", "status", "action"].includes(value) ? "center" : "start", sortable: false }));
    },
    canCancel() { return this.detail && [0, 1, 2].includes(this.detail.status) && !!this.detail.rowVersion; },
    totalQuantity() { return this.detail ? this.detail.items.reduce((sum, item) => sum + Number(item.amount || 0), 0) : 0; },
    meetingTime() {
      if (!this.detail || !this.detail.meetingStart) return "–";
      return `${this.timePart(this.detail.meetingStart)}–${this.timePart(this.detail.meetingEnd)}`;
    },
    staffInitials() {
      const name = this.detail && this.detail.staffName;
      return name ? name.split(/[\s.]+/).filter(Boolean).map(part => part[0]).join("").slice(0, 2).toUpperCase() : "–";
    },
    roomImage() {
      const image = String(this.detail?.roomImage || "").trim();
      if (!image) return "";
      if (/^(data:image\/|https?:\/\/|\/)/i.test(image)) return image;
      if (/^[A-Za-z0-9+/\s]+={0,2}$/.test(image) && image.length > 100) {
        const mime = image.startsWith("iVBOR") ? "png" : image.startsWith("UklGR") ? "webp" : "jpeg";
        return `data:image/${mime};base64,${image.replace(/\s/g, "")}`;
      }
      return "";
    },
  },
  watch: {
    "$route.params.orderId"() {
      this.cancelDialog = false;
      if (this.detailRequest) this.detailRequest.cancel();
      this.detail = null;
      this.detailError = "";
      if (this.isDetail) this.loadDetail();
      else this.loadOrders();
    },
  },
  mounted() { if (this.isDetail) this.loadDetail(); else this.loadOrders(); },
  beforeDestroy() {
    [this.listRequest, this.detailRequest, this.exportRequest].forEach(request => { if (request) request.cancel(); });
  },
  methods: {
    statusGroup(status) { return STATUS_GROUPS[Number(status)] || "unknown"; },
    statusLabel(status) { return this.$t(`manageOrder.${this.statusGroup(status)}`); },
    locationName(order) { return (this.$i18n.locale === "th" && order.locationNameTH) || order.locationName || "–"; },
    datePart(date) { return date && dayjs(date).isValid() ? dayjs(date).format("D/M/YYYY") : "–"; },
    timePart(date) { return date && dayjs(date).isValid() ? dayjs(date).format("HH:mm") : ""; },
    detailDate(date) {
      if (!date || !dayjs(date).isValid()) return "–";
      return new Intl.DateTimeFormat(this.$i18n.locale === "th" ? "th-TH-u-ca-gregory" : "en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }).format(dayjs(date).toDate());
    },
    itemSpecifications(item) {
      let sweetness = item.sweetnessName || "";
      if (item.sweetnessPercent !== null && item.sweetnessPercent !== undefined) sweetness += sweetness ? ` (${item.sweetnessPercent}%)` : `${item.sweetnessPercent}%`;
      return [item.temperatureName, sweetness, item.itemNote].filter(Boolean).join(" • ");
    },
    setDate(key, date) { this.filters[key] = date; this.dateMenus[key] = false; this.applyFilters(); },
    applyFilters() { this.filters.search = this.searchInput; this.page = 1; this.loadOrders(); },
    changePageSize() { this.page = 1; this.loadOrders(); },
    requestParams() {
      return Object.keys(this.filters).reduce((params, key) => {
        if (this.filters[key] !== null && this.filters[key] !== "") params[key] = this.filters[key];
        return params;
      }, {});
    },
    responseData(response) {
      if (response.data.status !== 0) throw new Error(response.data.message || this.$t("manageOrder.loadError"));
      return normalizeFields(response.data.data);
    },
    errorMessage(error, key) {
      if (error?.response?.status === 403) return this.$t("manageOrder.accessError");
      return error?.response?.data?.message || error?.message || this.$t(`manageOrder.${key}`);
    },
    async loadOrders() {
      if (this.listRequest) this.listRequest.cancel();
      const request = axios.CancelToken.source();
      this.listRequest = request;
      this.loading = true;
      this.listError = "";
      try {
        const response = await axios.get(`${enurl.apiUrl}Transaction/getManageOrders`, { params: { ...this.requestParams(), page: this.page, perpage: this.perpage }, cancelToken: request.token });
        const data = this.responseData(response);
        if (this.listRequest !== request) return;
        this.orders = (data.items || []).map(normalizeFields);
        this.totalRecord = Number(data.totalRecord || 0);
        if (this.page > this.pageCount) { this.page = this.pageCount; this.loadOrders(); }
      } catch (error) {
        if (!axios.isCancel(error) && this.listRequest === request) { this.orders = []; this.totalRecord = 0; this.listError = this.errorMessage(error, "loadError"); }
      } finally { if (this.listRequest === request) { this.loading = false; this.listRequest = null; } }
    },
    openDetail(orderId) { this.$router.push({ name: "ManageOrderDetail", params: { orderId: String(orderId) } }); },
    async loadDetail() {
      if (this.detailRequest) this.detailRequest.cancel();
      const request = axios.CancelToken.source();
      this.detailRequest = request;
      this.detailLoading = true;
      this.detailError = "";
      this.detail = null;
      this.roomImageFailed = false;
      try {
        const response = await axios.get(`${enurl.apiUrl}Transaction/getManageOrderDetail`, { params: { orderId: this.$route.params.orderId }, cancelToken: request.token });
        const data = this.responseData(response);
        if (this.detailRequest !== request || !this.isDetail) return;
        this.detail = { ...data, status: Number(data.status), items: (data.items || []).map(normalizeFields) };
      } catch (error) {
        if (!axios.isCancel(error) && this.detailRequest === request) this.detailError = this.errorMessage(error, "detailError");
      } finally { if (this.detailRequest === request) { this.detailLoading = false; this.detailRequest = null; } }
    },
    openCancel() { this.cancelReason = ""; this.cancelError = ""; this.cancelDialog = true; },
    async cancelOrder() {
      if (!this.canCancel || this.cancelling || !this.cancelReason.trim() || this.cancelReason.length > 500) return;
      const orderId = this.detail.orderID;
      const rowVersion = this.detail.rowVersion;
      this.cancelling = true;
      this.cancelError = "";
      try {
        const response = await axios.post(`${enurl.apiUrl}Transaction/updateManageOrderStatus`, { orderID: orderId, status: 4, rowVersion, cancelReason: this.cancelReason.trim() });
        this.responseData(response);
        this.cancelDialog = false;
        if (String(this.$route.params.orderId) === String(orderId)) await this.loadDetail();
      } catch (error) {
        if (error?.response?.status === 409) {
          this.cancelDialog = false;
          this.notice = this.$t("manageOrder.conflict"); this.noticeVisible = true;
          if (String(this.$route.params.orderId) === String(orderId)) await this.loadDetail();
        } else this.cancelError = this.errorMessage(error, "cancelError");
      } finally { this.cancelling = false; }
    },
    exportRow(order) {
      return [order.orderID, order.roomName || "", this.locationName(order), order.serviceArea || "", this.detailDate(order.createTime), this.detailDate(order.completedTime), order.staffName || "", this.statusLabel(order.status), (order.items || []).map(raw => { const item = normalizeFields(raw); return `${item.beverageName || ""} ×${item.amount || 0}`; }).join("; ")];
    },
    async exportOrders() {
      if (this.exporting) return;
      this.exporting = true;
      const request = axios.CancelToken.source();
      this.exportRequest = request;
      const selected = this.isDetail ? this.detail : null;
      const params = this.requestParams();
      try {
        const XLSX = await import("xlsx");
        const workbook = XLSX.utils.book_new();
        if (selected) {
          const columns = ["orderId", "requester", "location", "zone", "orderTime", "completedTime", "staff", "status", "exportItems"].map(key => this.$t(`manageOrder.${key}`));
          XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([columns, this.exportRow(selected)]), "Order");
          XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([[this.$t("manageOrder.itemDescription"), this.$t("manageOrder.quantity")], ...selected.items.map(item => [`${item.beverageName} ${this.itemSpecifications(item)}`, item.amount])]), "Items");
        } else {
          const rows = [[...headersForExport(this)]];
          let beforeId;
          let hasMore;
          // Cursor pagination bounds each request and avoids duplicates if new orders arrive.
          do {
            const response = await axios.get(`${enurl.apiUrl}Transaction/getManageOrders`, { params: { ...params, page: 1, perpage: 100, beforeId }, cancelToken: request.token });
            const data = this.responseData(response);
            const items = (data.items || []).map(normalizeFields);
            items.forEach(item => rows.push(this.exportRow(item)));
            hasMore = data.hasMore && items.length > 0;
            if (items.length) beforeId = Math.min(...items.map(item => Number(item.orderID)));
          } while (hasMore);
          XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(rows), "Orders");
        }
        request.token.throwIfRequested();
        XLSX.writeFile(workbook, `ManageOrder${selected ? `_${selected.orderID}` : ""}_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`);
      } catch (error) {
        if (!axios.isCancel(error)) { this.notice = this.errorMessage(error, "exportError"); this.noticeVisible = true; }
      } finally { this.exporting = false; this.exportRequest = null; }
    },
  },
};
function headersForExport(vm) {
  return ["orderId", "requester", "location", "zone", "orderTime", "completedTime", "staff", "status", "exportItems"].map(key => vm.$t(`manageOrder.${key}`));
}
</script>

<style scoped>
.manage-order-page { color: #292b2e; font-family: Inter, sans-serif; padding: 27px 0 0; }
.order-toolbar { display: grid; grid-template-columns: 1fr 1fr 1fr 1.4fr 140px; align-items: center; gap: 18px; }
.order-filter { min-width: 0; border-radius: 40px; box-shadow: 0 5px 7px #00000020; }
.order-filter ::v-deep .v-input__slot { min-height: 52px; padding: 0 22px !important; border-radius: 40px; }
.order-filter ::v-deep .v-input__icon .v-icon { color: #00378e; }
.order-search { display: flex; min-width: 0; height: 52px; align-items: center; gap: 8px; padding: 6px 8px 6px 25px; border-radius: 40px; background: #fff; box-shadow: 0 5px 7px #00000020; }
.order-search input { width: 100%; min-width: 0; outline: none; font-size: 16px; }
.order-search:focus-within { outline: 2px solid #00378e; outline-offset: 3px; }
.order-search button, .order-detail-button { display: grid; flex: none; place-items: center; border-radius: 50%; background: #00378e; }
.order-search button { width: 40px; height: 40px; }
.order-export.v-btn { height: 52px; min-width: 135px; background: #00378e !important; color: white; text-transform: none; font-size: 18px; letter-spacing: normal; box-shadow: 0 5px 7px #00000020; }
.order-export.v-btn.v-btn--disabled { opacity: .55; }
.order-list-panel { margin-top: 64px; padding: 24px 16px 30px; border-radius: 28px; background: white; box-shadow: 0 6px 8px #00000020; }
.manage-order-table { overflow: hidden; border-radius: 28px 28px 0 0; }
.manage-order-table ::v-deep table { min-width: 1040px; table-layout: fixed; }
.manage-order-table ::v-deep thead th { height: 72px; border-bottom: 0 !important; background: #00378e; color: white !important; font-weight: 400; font-size: 15px; }
.manage-order-table ::v-deep tbody td { height: 57px; padding: 10px 14px; border-bottom: 0 !important; color: #505050; font-size: 14px; overflow-wrap: anywhere; }
.manage-order-table ::v-deep tbody tr:nth-child(even) { background: #f6f6f6; }
.manage-order-table ::v-deep th:first-child { width: 6%; }
.manage-order-table ::v-deep th:nth-child(2) { width: 8%; }
.manage-order-table ::v-deep th:nth-child(3), .manage-order-table ::v-deep th:nth-child(4) { width: 13%; }
.manage-order-table ::v-deep th:nth-child(5) { width: 10%; }
.manage-order-table ::v-deep th:nth-child(6), .manage-order-table ::v-deep th:nth-child(7) { width: 12%; }
.manage-order-table ::v-deep th:nth-child(8) { width: 10%; }
.manage-order-table ::v-deep th:nth-child(9) { width: 10%; }
.manage-order-table ::v-deep th:last-child { width: 6%; }
.order-date { display: block; line-height: 1.25; white-space: nowrap; }
.order-date small { display: block; font-size: inherit; }
.order-status-cell { display: flex; flex-direction: column; align-items: center; gap: 2px; }
.order-status-cell small { color: #626b80; font-size: 11px; }
.order-status { display: inline-flex; align-items: center; justify-content: center; min-width: 85px; padding: 7px 10px; border-radius: 30px; font-size: 12px; font-weight: 500; white-space: nowrap; }
.order-status.new { background: #c4dbff; color: #244e84; }
.order-status.ready { background: #ead77c; color: #645316; }
.order-status.completed { background: #8bca7b; color: #24531e; }
.order-status.rejected { background: #ff898e; color: #7b1820; }
.order-status.unknown { background: #eee; color: #555; }
.order-detail-button { width: 34px; height: 34px; margin: auto; }
.order-detail-button:focus-visible { outline: 3px solid #7badff; outline-offset: 3px; }
.order-pagination { position: relative; display: flex; align-items: center; justify-content: center; min-height: 85px; margin-top: 24px; padding: 0 145px; }
.order-pagination ::v-deep .v-pagination__item, .order-pagination ::v-deep .v-pagination__navigation { border-radius: 12px; min-width: 44px; height: 44px; }
.order-page-size { position: absolute; right: 6px; width: 120px; flex: none; border-radius: 30px; background: #f6f6f6; box-shadow: 0 3px 3px #00000020; }
.order-page-size ::v-deep .v-input__slot { border-radius: 30px; background: #f6f6f6 !important; }
.order-alert { margin: 24px 0 0; }
.order-detail-toolbar { display: flex; justify-content: space-between; align-items: center; gap: 20px; margin: 8px 0 36px; }
.order-detail-heading { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.order-detail-heading h2 { font-size: 23px; font-weight: 700; margin: 0; }
.detail-status { min-width: 0; padding: 5px 10px; font-size: 11px; gap: 6px; text-transform: uppercase; }
.status-dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
.order-detail-actions { display: flex; gap: 14px; }
.order-detail-actions .order-export { height: 36px; min-width: 120px; font-size: 16px; }
.order-cancel.v-btn { height: 36px; background: #fce8e6 !important; color: #c62828; text-transform: none; letter-spacing: normal; box-shadow: 0 4px 6px #00000018; }
.order-detail-grid { display: grid; grid-template-columns: minmax(0, 1.65fr) minmax(0, 1fr); align-items: stretch; gap: 20px; }
.order-information-column { display: flex; flex-direction: column; gap: 10px; min-width: 0; }
.order-card { border-radius: 12px; background: #fff; padding: 24px; }
.order-card h3 { display: flex; align-items: center; gap: 8px; margin: 0 0 14px; font-size: 18px; font-weight: 600; }
.order-information { min-height: 295px; }
.order-requester { padding: 20px 22px; background: #f6f6f6; }
.order-label, .order-information-fields dt, .order-items-table-header { color: #69748a; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; }
.requester-content { display: flex; align-items: center; gap: 8px; margin-top: 6px; }
.requester-content img, .room-placeholder { flex: none; width: 34px; height: 34px; object-fit: cover; border-radius: 5px; }
.requester-content strong, .staff-profile strong { display: block; font-size: 14px; font-weight: 500; }
.requester-content small { display: block; font-size: 10px; font-weight: 600; margin-top: 2px; text-transform: uppercase; }
.order-information-fields { display: grid; grid-template-columns: .8fr 1.4fr 1.1fr .8fr; gap: 18px; margin-top: 24px; }
.order-information-fields dd { display: flex; align-items: flex-start; gap: 5px; margin: 5px 0 0; font-size: 13px; overflow-wrap: anywhere; }
.order-information-fields .v-icon { color: #69748a; }
.order-zone-icon { width: 16px; height: 16px; flex: none; object-fit: contain; }
.order-staff { min-height: 114px; padding: 16px; box-shadow: 0 2px 1px #00000020; }
.staff-profile { display: flex; align-items: center; gap: 10px; }
.staff-avatar { display: grid; place-items: center; width: 38px; height: 38px; border-radius: 50%; background: #dce2ff; font-size: 13px; font-weight: 700; }
.staff-profile small { display: block; color: #69748a; font-size: 11px; }
.staff-service-area { margin: 15px 8px 0; font-size: 11px; }
.staff-service-area span { display: block; color: #69748a; }
.staff-service-area strong { font-weight: 500; }
.order-items { min-height: 419px; }
.order-items-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
.order-items-heading > span { margin-top: 3px; padding: 2px 10px; border-radius: 20px; background: #eceef1; color: #626d82; font-size: 10px; font-weight: 600; text-align: center; }
.order-items-table-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 9px 5px; background: #f1f3f5; margin-top: 10px; }
.order-items-list { list-style: none; margin: 0; padding: 0; }
.order-items-list li { display: flex; justify-content: space-between; gap: 15px; padding: 22px 14px 10px; }
.order-items-list strong { font-size: 14px; font-weight: 600; }
.order-items-list p { margin: 3px 0 0; color: #69748a; font-size: 11px; overflow-wrap: anywhere; white-space: pre-line; }
.order-item-quantity { white-space: nowrap; }
.cancel-reason-text { white-space: pre-wrap; overflow-wrap: anywhere; }
@media (max-width: 1200px) {
  .order-toolbar { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .order-search { grid-column: span 2; }
  .order-list-panel { margin-top: 36px; }
  .order-detail-grid { grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr); }
  .order-information-fields { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .order-items-heading { flex-wrap: wrap; }
}
@media (max-width: 800px) {
  .order-detail-grid { grid-template-columns: minmax(0, 1fr); }
  .order-items { min-height: 0; }
  .order-detail-toolbar { flex-wrap: wrap; }
}
@media (max-width: 600px) {
  .manage-order-page { padding-top: 8px; }
  .order-toolbar { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
  .order-search { grid-column: span 2; grid-row: 3; }
  .order-export.v-btn { min-width: 0; font-size: 16px; }
  .order-filter ::v-deep .v-input__slot { padding: 0 12px !important; }
  .order-list-panel { margin-top: 24px; padding: 12px 10px; border-radius: 18px; }
  .order-pagination { flex-wrap: wrap; gap: 16px; padding: 0; }
  .order-page-size { position: static; flex: 0 0 100px; }
  .order-pagination ::v-deep .v-pagination__item, .order-pagination ::v-deep .v-pagination__navigation { min-width: 32px; height: 36px; margin: 3px; }
  .order-card { padding: 18px; }
  .order-detail-toolbar { margin-bottom: 24px; }
  .order-detail-heading h2 { font-size: 21px; }
  .order-requester { padding: 16px; }
  .order-information-fields { gap: 18px 12px; }
}
</style>
